import { useTheme } from '@amzn/meridian/theme';
import themeTokens from '@amzn/meridian-tokens/theme/definition';
import { ComparisonStatus } from './dashboard/constants';

/**
 * The colours the Flow Monitoring pages draw with, read from Meridian's theme tokens so each one
 * follows the app's light / dark mode (`<Theme mode>` in ThemeProvider). A hardcoded hex stays
 * light-mode coloured on a dark page, which is what this replaces.
 */
export interface FlowColors {
    /** A health status as ink: a card's accent stripe, a tile's value. */
    statusAccent: (status: string | undefined) => string;
    /** A health status as a subtle wash behind a card. */
    statusBackground: (status: string | undefined) => string;
    /** The page/card surface. */
    surface: string;
    /** A hairline border or track. */
    hairline: string;
    /** Secondary text. */
    inkSubdued: string;
    /** The selected / live / primary-action colour. */
    primary: string;
    /** An idle connector or control. */
    idle: string;
    /** A disabled control. */
    disabled: string;
    /** A highlighted section header. */
    highlight: string;
    /** A pressed toggle's fill. */
    pressed: string;
}

type TokenHelper = (key: string) => string;

export const useFlowColors = (): FlowColors => {
    const t = useTheme(themeTokens as object) as unknown as TokenHelper;
    const success = t('themeSupportInkSuccess');
    const accents: Record<string, string> = {
        [ComparisonStatus.AHEAD]: success,
        [ComparisonStatus.ON_TRACK]: success,
        [ComparisonStatus.AT_RISK]: t('themeSupportInkWarning'),
        [ComparisonStatus.CRITICAL]: t('themeSupportInkError'),
    };
    const successFill = t('themeSupportFillSuccess');
    const backgrounds: Record<string, string> = {
        [ComparisonStatus.AHEAD]: successFill,
        [ComparisonStatus.ON_TRACK]: successFill,
        [ComparisonStatus.AT_RISK]: t('themeSupportFillWarning'),
        [ComparisonStatus.CRITICAL]: t('themeSupportFillError'),
    };
    const inkSubdued = t('themeInkSubdued');
    const neutralBackground = t('themeSurfaceDisabled');
    return {
        statusAccent: (status) => (status && accents[status]) || inkSubdued,
        statusBackground: (status) => (status && backgrounds[status]) || neutralBackground,
        surface: t('themeSurfaceDefault'),
        hairline: t('themeBorderSubdued'),
        inkSubdued,
        primary: t('themeActionPrimaryDefault'),
        idle: inkSubdued,
        disabled: t('themeBorderDisabled'),
        highlight: t('themeSurfacePalette'),
        pressed: t('themeDecorativePaletteSubdued'),
    };
};
